import createTheme from '@mui/material/styles/createTheme';
import { alpha } from '@mui/material/styles';

// TypeScript module augmentation for custom button variant
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    floating: true;
  }
}

export default createTheme({
  typography: {
    fontFamily: 'Inter, sans-serif',
    h1: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    h4: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    h5: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    h6: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    button: {
      fontFamily: 'Cabinet Grotesk, sans-serif',
      fontWeight: 700,
    },
    fontWeightBold: 700,
  },
  palette: {
    primary: {
      main: '#382767',
      light: '#ff00d4',
      dark: '#2b00ff',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#274a67', // Adding a darker indigo for accountant messages
      contrastText: '#ffffff',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: 'Inter, sans-serif',
        },
        strong: {
          fontFamily: 'Inter, sans-serif',
          fontWeight: 700,
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'floating' },
          style: {
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease',
            backgroundColor: '#ffffff',
            color: '#382767',
            borderRadius: '8px',
            border: '1px solid rgba(56, 39, 103, 0.1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              backgroundColor: '#ffffff',
              color: '#382767',
              boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
            },
            '&:active': {
              transform: 'translateY(0)',
              backgroundColor: '#f0f0f0',
              color: '#382767',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#ffffff',
              color: 'rgba(56, 39, 103, 0.5)',
              border: '1px solid rgba(56, 39, 103, 0.1)',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          textTransform: 'none', // Prevents all-caps text
          borderRadius: '8px', // Slightly rounded corners
          transition: 'all 0.15s ease-in-out',
          whiteSpace: 'nowrap',
          // Add touch action for better mobile performance
          touchAction: 'manipulation',
          // Ensure active states work on mobile
          '&:active': {
            transform: 'scale(0.98)',
            transition: 'all 0.1s ease-in-out',
          },
          // Add focus visible for accessibility
          '&:focus-visible': {
            outline: '2px solid #382767',
            outlineOffset: '2px',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
            backgroundColor: '#4a3485', // Using the light shade instead of dark
            transform: 'translateY(-1px)',
          },
          '&:active': {
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)',
            transform: 'translateY(0) scale(0.98)',
            backgroundColor: '#3a2a6a',
          },
        },
        outlined: {
          borderColor: alpha('#382767', 0.5),
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#ffffff',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 3px rgba(0, 0, 0, 0.2)',
            borderColor: '#382767',
          },
          '&:active': {
            backgroundColor: '#f8f9fa',
            transform: 'translateY(0) scale(0.98)',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1)',
            borderColor: '#382767',
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha('#382767', 0.08),
            transform: 'translateY(-1px)',
          },
          '&:active': {
            backgroundColor: alpha('#382767', 0.16),
            transform: 'translateY(0) scale(0.98)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          transition: 'all 0.2s ease-in-out',
          color: '#4A5568',
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#CBD5E0',
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#718096',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#382767',
          },
          '& .MuiSvgIcon-root': {
            color: '#718096',
          },
          '&.Mui-focused': {
            color: '#382767',
            '& .MuiSvgIcon-root': {
              color: '#382767',
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#718096',
          '&.Mui-focused': {
            color: '#382767',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: '8px',
          marginTop: '8px',
          padding: '4px',
          boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
          backgroundColor: '#ffffff',
        },
        list: {
          padding: '4px',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '12px 16px',
          transition: 'all 0.2s ease-in-out',
          borderRadius: '6px',
          '&.Mui-selected': {
            backgroundColor: alpha('#382767', 0.08),
            color: '#382767',
            '&:hover': {
              backgroundColor: alpha('#382767', 0.12),
            },
          },
          '&:hover': {
            backgroundColor: alpha('#382767', 0.04),
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha('#382767', 0.12),
          },
          '&:active': {
            backgroundColor: alpha('#382767', 0.16),
          },
          '& .MuiTouchRipple-root': {
            color: alpha('#382767', 0.3),
          },
          '& .MuiTouchRipple-rippleVisible': {
            opacity: 0.5,
            animationDuration: '550ms',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
          border: '1px solid #FFFFFF16',
          padding: 2,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: alpha('#382767', 0.12),
          '&.MuiDivider-vertical': {
            borderColor: alpha('#382767', 0.12),
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          transition: 'all 0.2s ease-in-out',
          color: '#4A5568',
          backgroundColor: '#ffffff',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#CBD5E0',
            borderRadius: '8px',
            transition: 'all 0.2s ease-in-out',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#718096',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#382767',
          },
          '&.Mui-focused': {
            color: '#382767',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: '#382767',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: alpha('#382767', 0.04),
          },
          '&.Mui-disabled': {
            color: alpha('#382767', 0.3),
          },
        },
      },
    },
  },
});
